import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from './surveyCreatorApi';
import type {
  ChatResult,
  CreatorMessage,
  CreatorSessionSummary,
  DraftVersion,
  EditResult,
  Phase,
  SaveResult,
  StructuredProgress,
  TaskStatusResp,
  VersionSource,
} from './types';

const POLL_MS = 1200;

interface UseSurveyCreatorOptions {
  initialSessionId?: number;
  enabled?: boolean;
  onSessionChange?: (id: number | null) => void;
  onSurveyCreated?: (surveyId: number) => void;
}

export function useSurveyCreator(opts: UseSurveyCreatorOptions = {}) {
  const { initialSessionId, enabled = true, onSessionChange, onSurveyCreated } = opts;

  const [sessions, setSessions] = useState<CreatorSessionSummary[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(initialSessionId ?? null);
  const [messages, setMessages] = useState<CreatorMessage[]>([]);

  const [draftMarkdown, setDraftMarkdownState] = useState('');
  // Mirror of draftMarkdown for use inside async poll callbacks, whose captured `draftMarkdown`
  // goes stale if a highlight edit / manual change lands while a chat turn is in flight.
  const draftMarkdownRef = useRef('');
  useEffect(() => {
    draftMarkdownRef.current = draftMarkdown;
  }, [draftMarkdown]);
  const [versions, setVersions] = useState<DraftVersion[]>([]);
  const [versionIdx, setVersionIdx] = useState(-1);
  // Mirror of versionIdx for use inside stable callbacks (pushVersion has [] deps).
  const versionIdxRef = useRef(-1);
  useEffect(() => {
    versionIdxRef.current = versionIdx;
  }, [versionIdx]);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [createdSurveyId, setCreatedSurveyId] = useState<number | null>(null);
  const [phase, setPhase] = useState<Phase>('clarifying');

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState<StructuredProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastEditedRange, setLastEditedRange] = useState<{ start: number; end: number } | null>(
    null,
  );

  // Refs to ignore stale poll results when the user switches sessions / starts a new task.
  const timersRef = useRef<Set<ReturnType<typeof setInterval>>>(new Set());
  const chatTaskRef = useRef<string | null>(null);
  const editTaskRef = useRef<string | null>(null);
  const saveTaskRef = useRef<string | null>(null);

  const stopAllPolling = useCallback(() => {
    timersRef.current.forEach((t) => clearInterval(t));
    timersRef.current.clear();
  }, []);

  useEffect(() => () => stopAllPolling(), [stopAllPolling]);

  const startPolling = useCallback(
    <T>(
      taskId: string,
      isCurrent: () => boolean,
      onDone: (result: T) => void,
      onErr: (msg: string) => void,
    ) => {
      const interval = setInterval(async () => {
        if (!isCurrent()) {
          stop();
          return;
        }
        try {
          const status: TaskStatusResp<T> = await api.getTaskStatus<T>(taskId);
          // Keep polling for any non-terminal state (PENDING, PROGRESS, or anything unexpected
          // like STARTED/RETRY); only SUCCESS and FAILURE are terminal.
          if (status.state !== 'SUCCESS' && status.state !== 'FAILURE') {
            if (status.structured_progress) setProgress(status.structured_progress);
            return;
          }
          stop();
          if (!isCurrent()) return;
          if (status.state === 'SUCCESS' && status.result) onDone(status.result);
          else onErr(status.error || 'The task failed.');
        } catch (e) {
          stop();
          onErr(e instanceof Error ? e.message : 'Network error while polling.');
        }
      }, POLL_MS);
      const stop = () => {
        clearInterval(interval);
        timersRef.current.delete(interval);
      };
      timersRef.current.add(interval);
    },
    [],
  );

  // ----- draft / version management (client markdown is canonical at save time) -----
  const pushVersion = useCallback((markdown: string, source: VersionSource) => {
    setVersions((prev) => {
      // Truncate any redo tail first: a new edit after an undo discards the
      // versions that were undone past (otherwise undo resurrects orphaned drafts).
      const head = prev.slice(0, versionIdxRef.current + 1);
      if (head.length && head[head.length - 1].markdown === markdown) return prev;
      const next = [...head, { markdown, source, version: head.length + 1 }];
      versionIdxRef.current = next.length - 1;
      setVersionIdx(next.length - 1);
      return next;
    });
    setDraftMarkdownState(markdown);
    if (markdown.trim()) setCanvasOpen(true);
  }, []);

  const setManualDraft = useCallback(
    (markdown: string) => {
      if (markdown !== draftMarkdown) pushVersion(markdown, 'manual');
    },
    [draftMarkdown, pushVersion],
  );

  const canUndo = versionIdx > 0;
  const canRedo = versionIdx >= 0 && versionIdx < versions.length - 1;

  const undo = useCallback(() => {
    setVersionIdx((idx) => {
      if (idx <= 0) return idx;
      setDraftMarkdownState(versions[idx - 1].markdown);
      return idx - 1;
    });
  }, [versions]);

  const redo = useCallback(() => {
    setVersionIdx((idx) => {
      if (idx >= versions.length - 1) return idx;
      setDraftMarkdownState(versions[idx + 1].markdown);
      return idx + 1;
    });
  }, [versions]);

  // ----- sessions -----
  const refreshSessions = useCallback(async () => {
    try {
      const { sessions: list } = await api.listSessions();
      setSessions(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sessions.');
    }
  }, []);

  const resetDraftState = useCallback(() => {
    setMessages([]);
    setDraftMarkdownState('');
    setVersions([]);
    setVersionIdx(-1);
    setCanvasOpen(false);
    setCreatedSurveyId(null);
    setPhase('clarifying');
    setProgress(null);
    setError(null);
    setLastEditedRange(null);
  }, []);

  const openSession = useCallback(
    async (id: number) => {
      stopAllPolling();
      resetDraftState();
      setSessionId(id);
      onSessionChange?.(id);
      try {
        const detail = await api.getSession(id);
        setMessages(detail.messages || []);
        setPhase((detail.phase as Phase) || 'clarifying');
        setCreatedSurveyId(detail.created_survey_id);
        if (detail.draft_markdown?.trim()) {
          setVersions([{ markdown: detail.draft_markdown, source: 'agent', version: 1 }]);
          setVersionIdx(0);
          setDraftMarkdownState(detail.draft_markdown);
          setCanvasOpen(true);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to open session.');
      }
    },
    [onSessionChange, resetDraftState, stopAllPolling],
  );

  const newSession = useCallback(async () => {
    stopAllPolling();
    resetDraftState();
    try {
      const created = await api.createSession();
      setSessionId(created.id);
      onSessionChange?.(created.id);
      await refreshSessions();
      return created.id;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create session.');
      return null;
    }
  }, [onSessionChange, refreshSessions, resetDraftState, stopAllPolling]);

  const removeSession = useCallback(
    async (id: number) => {
      try {
        await api.deleteSession(id);
        if (id === sessionId) {
          // Stop in-flight polls and forget their task ids first — otherwise a chat/edit/save
          // result that lands after deletion would resurrect content into the now-empty session.
          stopAllPolling();
          chatTaskRef.current = null;
          editTaskRef.current = null;
          saveTaskRef.current = null;
          setSessionId(null);
          onSessionChange?.(null);
          resetDraftState();
        }
        await refreshSessions();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete session.');
      }
    },
    [onSessionChange, refreshSessions, resetDraftState, sessionId, stopAllPolling],
  );

  const renameSession = useCallback(
    async (id: number, title: string) => {
      try {
        await api.renameSession(id, title);
        await refreshSessions();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to rename session.');
      }
    },
    [refreshSessions],
  );

  // ----- chat turn -----
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading) return;
      let activeId = sessionId;
      if (activeId == null) activeId = await newSession();
      if (activeId == null) return;

      setError(null);
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
      setIsLoading(true);
      setProgress(null);
      try {
        // Send the draft the user currently sees (incl. canvas free-edits) as the canonical base,
        // so a manual edit followed by a chat message isn't revised against a stale server draft.
        const { task_id } = await api.sendMessage(activeId, trimmed, draftMarkdownRef.current);
        chatTaskRef.current = task_id;
        startPolling<ChatResult>(
          task_id,
          () => chatTaskRef.current === task_id,
          (result) => {
            setIsLoading(false);
            setProgress(null);
            if (result.response) {
              setMessages((prev) => [...prev, { role: 'assistant', content: result.response }]);
            }
            if (result.phase) setPhase(result.phase);
            if (result.created_survey_id) setCreatedSurveyId(result.created_survey_id);
            // Compare against the live draft (ref), not the value captured when the turn started —
            // a highlight/manual edit may have landed mid-turn.
            if (result.draft_markdown && result.draft_markdown !== draftMarkdownRef.current) {
              pushVersion(result.draft_markdown, 'agent');
            }
            refreshSessions();
          },
          (msg) => {
            setIsLoading(false);
            setProgress(null);
            setError(msg);
          },
        );
      } catch (e) {
        setIsLoading(false);
        // Dispatch failed — roll back the optimistic user bubble so it isn't left dangling.
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          return last && last.role === 'user' && last.content === trimmed
            ? prev.slice(0, -1)
            : prev;
        });
        setError(e instanceof Error ? e.message : 'Failed to send message.');
      }
    },
    [isLoading, newSession, pushVersion, refreshSessions, sessionId, startPolling],
  );

  // ----- targeted highlight edit -----
  const applyHighlightEdit = useCallback(
    async (params: {
      instruction: string;
      selectedText: string;
      occurrence?: number;
      selectionStart?: number;
      selectionEnd?: number;
      prefix?: string;
      suffix?: string;
      baseMarkdown?: string;
    }) => {
      if (sessionId == null || isEditing) return;
      setError(null);
      setIsEditing(true);
      try {
        const { task_id } = await api.applyEdit(sessionId, {
          instruction: params.instruction,
          selected_text: params.selectedText,
          occurrence: params.occurrence ?? 0,
          // For a raw-editor (offset) edit the base IS the textarea value the offsets index;
          // otherwise the committed draft that the preview renders.
          base_markdown: params.baseMarkdown ?? draftMarkdown,
          selection_start: params.selectionStart ?? null,
          selection_end: params.selectionEnd ?? null,
          prefix: params.prefix,
          suffix: params.suffix,
        });
        editTaskRef.current = task_id;
        startPolling<EditResult>(
          task_id,
          () => editTaskRef.current === task_id,
          (result) => {
            setIsEditing(false);
            if (result.full_markdown) {
              pushVersion(result.full_markdown, 'highlight');
              if (typeof result.start === 'number' && typeof result.end === 'number') {
                setLastEditedRange({ start: result.start, end: result.end });
                window.setTimeout(() => setLastEditedRange(null), 2000);
              }
            }
          },
          (msg) => {
            setIsEditing(false);
            setError(msg);
          },
        );
      } catch (e) {
        setIsEditing(false);
        setError(e instanceof Error ? e.message : 'Failed to apply edit.');
      }
    },
    [draftMarkdown, isEditing, pushVersion, sessionId, startPolling],
  );

  // ----- save → create survey -----
  const saveSurvey = useCallback(
    async (payload: { name?: string; title?: string; description?: string } = {}) => {
      if (sessionId == null || isSaving || !draftMarkdown.trim()) return;
      setError(null);
      setIsSaving(true);
      try {
        const { task_id } = await api.saveSurvey(sessionId, {
          ...payload,
          markdown: draftMarkdown,
        });
        saveTaskRef.current = task_id;
        startPolling<SaveResult>(
          task_id,
          () => saveTaskRef.current === task_id,
          (result) => {
            setIsSaving(false);
            if (result.survey_id) {
              setCreatedSurveyId(result.survey_id);
              setPhase('saved');
              onSurveyCreated?.(result.survey_id);
            } else {
              setError('Save did not return a survey id.');
            }
          },
          (msg) => {
            setIsSaving(false);
            // Save runs an AI structuring step that can transiently time out — make the message
            // actionable instead of a bare "task failed".
            const generic = !msg || /task failed/i.test(msg);
            setError(
              generic
                ? "Couldn't create the survey — the AI structuring step may have timed out. Please click Create survey again."
                : `Couldn't create the survey: ${msg}`,
            );
          },
        );
      } catch (e) {
        setIsSaving(false);
        setError(e instanceof Error ? e.message : 'Failed to save survey.');
      }
    },
    [draftMarkdown, isSaving, onSurveyCreated, sessionId, startPolling],
  );

  // initial load — gated by `enabled` so a non-admin (redirected) never fires requests.
  useEffect(() => {
    if (!enabled) return;
    refreshSessions();
    if (initialSessionId != null) openSession(initialSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    // state
    sessions,
    sessionId,
    messages,
    draftMarkdown,
    versions,
    versionIdx,
    canvasOpen,
    createdSurveyId,
    phase,
    isLoading,
    isEditing,
    isSaving,
    progress,
    error,
    lastEditedRange,
    canUndo,
    canRedo,
    // actions
    refreshSessions,
    openSession,
    newSession,
    removeSession,
    renameSession,
    sendMessage,
    applyHighlightEdit,
    saveSurvey,
    setManualDraft,
    undo,
    redo,
    setError,
  };
}
